import type { CSSProperties } from 'react';
import {
  BookOpen,
  ChevronRight,
  Flame,
  Medal,
  ShoppingBag,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy
} from 'lucide-react';
import type { AppState, Child, KnowledgeType } from '../types';
import { ChildAvatar, ProgressRing, SectionTitle } from '../components/ui';
import {
  calculateTotalMastered,
  getDueCount,
  getPlanCount,
  getParentMetrics,
  getTodayReviewEvents,
  getWeeklyActivity
} from '../lib/srs';

const typeInfo: Record<KnowledgeType, { label: string; emoji: string; color: string }> = {
  character: { label: '汉字', emoji: '汉', color: '#ff8c73' },
  poem: { label: '古诗', emoji: '诗', color: '#7c6bd6' },
  word: { label: '英文', emoji: 'A', color: '#2f9d88' }
};

export function HomeView({
  state,
  activeChild,
  onSelectChild,
  onStart,
  onOpenProfile
}: {
  state: AppState;
  activeChild?: Child;
  onSelectChild: (childId: string) => void;
  onStart: () => void;
  onOpenProfile: (page: 'children' | 'plan' | 'shop' | 'shopRecords') => void;
}) {
  if (!activeChild) {
    return (
      <section className="empty-page">
        <div className="empty-page__art">🌱</div>
        <h1>先种下第一颗记忆种子</h1>
        <p>最多可以添加两名儿童，每个孩子会拥有独立的星星和学习进度。</p>
        <button className="primary-button" type="button" onClick={() => onOpenProfile('children')}>
          添加儿童
          <ChevronRight size={18} />
        </button>
      </section>
    );
  }

  const totals = calculateTotalMastered(state, activeChild.id);
  const metrics = getParentMetrics(state, activeChild.id);
  const todayEvents = getTodayReviewEvents(state, activeChild.id);
  const activity = getWeeklyActivity(state, activeChild.id);
  const maxActivity = Math.max(1, ...activity.map((entry) => entry.count));
  const dueByType = {
    character: getDueCount(state, activeChild.id, 'character'),
    poem: getDueCount(state, activeChild.id, 'poem'),
    word: getDueCount(state, activeChild.id, 'word')
  };
  const planByType = {
    character: getPlanCount(state, 'character', activeChild.id),
    poem: getPlanCount(state, 'poem', activeChild.id),
    word: getPlanCount(state, 'word', activeChild.id)
  };
  const totalDue = Object.values(dueByType).reduce((sum, count) => sum + count, 0);
  const totalPlan = Object.values(planByType).reduce((sum, count) => sum + count, 0);
  const todayKnown = todayEvents.filter((event) => event.result === 'known').length;
  const completion = totalDue ? Math.round((todayKnown / totalDue) * 100) : 100;

  const childStats = state.children.map((child) => {
    const childTotals = calculateTotalMastered(state, child.id);
    return {
      child,
      stars: child.stars,
      mastered: childTotals.character + childTotals.poem + childTotals.word,
      reviews: getTodayReviewEvents(state, child.id).length,
      masteryRate: getParentMetrics(state, child.id).masteryRate
    };
  });

  return (
    <div className="home-view">
      <section className="home-hero">
        <div className="home-hero__copy">
          <span className="eyebrow">今天也要记得牢</span>
          <h1>{getGreeting()}，{activeChild.name}</h1>
          <p>
            {!totalPlan
              ? '还没有安排学习计划，先去知识库挑选这周要学习的内容。'
              : totalDue
                ? `今天有 ${totalDue} 颗知识种子需要复习，完成后星星会被收进口袋。`
                : '学习计划里的任务已经完成啦，可以继续安排新的内容。'}
          </p>
          <button className="primary-button" type="button" onClick={totalPlan ? onStart : () => onOpenProfile('plan')}>
            <Sparkles size={18} />
            {totalPlan ? '开始今日学习' : '安排学习计划'}
          </button>
        </div>
        <div className="home-hero__summary">
          <div className="home-hero__progress">
            <ProgressRing value={todayKnown} max={Math.max(totalDue, todayKnown)} label="今日完成" />
            <div className="hero-progress-note">
              <strong>{completion}%</strong>
              <span>今日任务进度</span>
            </div>
          </div>
          <div className="home-hero__stars" aria-label={`${activeChild.stars} 个星星`}>
            <Star fill="currentColor" size={30} />
            <span>
              <small>我的星星</small>
              <strong>{activeChild.stars}</strong>
            </span>
          </div>
          <button className="home-hero__shop" type="button" onClick={() => onOpenProfile('shop')}>
            <ShoppingBag size={18} />
            <span>星星商店</span>
            <ChevronRight size={17} />
          </button>
        </div>
      </section>

      <main className="home-content">
          <article className="panel today-panel">
            <SectionTitle
              eyebrow="今日任务"
              title={!totalPlan ? '先安排学习计划' : totalDue ? '复习任务已经排好队' : '今天没有待复习内容'}
              action={<span className="task-count">{totalPlan ? `${totalDue} 项` : '未安排'}</span>}
            />
            <div className="task-strip">
              {(Object.keys(typeInfo) as KnowledgeType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  className="task-tile"
                  style={{ '--task-color': typeInfo[type].color } as CSSProperties}
                  onClick={totalPlan ? onStart : () => onOpenProfile('plan')}
                >
                  <span>{typeInfo[type].emoji}</span>
                  <div>
                    <strong>{typeInfo[type].label}复习</strong>
                    <small>{planByType[type] ? dueByType[type] ? `${dueByType[type]} 项待完成` : '今日已清空' : '尚未加入学习计划'}</small>
                  </div>
                  <ChevronRight size={18} />
                </button>
              ))}
            </div>
          </article>

          <section className="learning-totals">
            <article className="total-card total-card--character">
              <span className="total-card__icon">汉</span>
              <div>
                <strong>{totals.character}</strong>
                <small>认识汉字</small>
              </div>
            </article>
            <article className="total-card total-card--poem">
              <span className="total-card__icon">诗</span>
              <div>
                <strong>{totals.poem}</strong>
                <small>记住古诗</small>
              </div>
            </article>
            <article className="total-card total-card--word">
              <span className="total-card__icon">A</span>
              <div>
                <strong>{totals.word}</strong>
                <small>掌握单词</small>
              </div>
            </article>
          </section>

          <article className="panel dashboard-panel">
            <SectionTitle
              eyebrow="家长数据面板"
              title={`${activeChild.name}的学习表现`}
              action={
                <button className="text-button" type="button" onClick={() => onOpenProfile('shopRecords')}>
                  兑换记录
                  <ChevronRight size={16} />
                </button>
              }
            />
            <div className="metric-grid">
              <article>
                <span className="metric-icon metric-icon--blue"><BookOpen size={18} /></span>
                <small>今日复习</small>
                <strong>{metrics.reviewCount}<em> 次</em></strong>
              </article>
              <article>
                <span className="metric-icon metric-icon--green"><Target size={18} /></span>
                <small>掌握率</small>
                <strong>{metrics.masteryRate}<em>%</em></strong>
              </article>
              <article>
                <span className="metric-icon metric-icon--orange"><Flame size={18} /></span>
                <small>遗忘节点</small>
                <strong>{metrics.forgottenCount}<em> 次</em></strong>
              </article>
              <article>
                <span className="metric-icon metric-icon--purple"><TrendingUp size={18} /></span>
                <small>待复习</small>
                <strong>{metrics.dueCount}<em> 项</em></strong>
              </article>
            </div>

            <div className="dashboard-split">
              <div className="weekly-chart">
                <header>
                  <strong>近 7 天复习量</strong>
                  <small>蓝色为正确，橙色为忘记</small>
                </header>
                <div className="bar-chart" aria-label="近七天复习量柱状图">
                  {activity.map((entry) => (
                    <div className="bar-chart__column" key={entry.date.toISOString()}>
                      <div className="bar-chart__track">
                        <span
                          className="bar-chart__known"
                          style={{ height: `${Math.max(entry.correct ? 12 : 2, (entry.correct / maxActivity) * 100)}%` }}
                        />
                        <span
                          className="bar-chart__forgot"
                          style={{
                            height: `${Math.max(entry.count - entry.correct ? 12 : 2, ((entry.count - entry.correct) / maxActivity) * 100)}%`
                          }}
                        />
                      </div>
                      <small>{entry.label}</small>
                      <b>{entry.count}</b>
                    </div>
                  ))}
                </div>
              </div>

              <div className="difficult-list">
                <header>
                  <strong>难点知识</strong>
                  <small>优先复习容易忘记的内容</small>
                </header>
                {metrics.difficultItems.length ? (
                  metrics.difficultItems.slice(0, 4).map((entry) => (
                    <div className="difficult-item" key={entry.item.id}>
                      <span className={`knowledge-symbol knowledge-symbol--${entry.item.type}`}>
                        {entry.item.type === 'poem' ? '诗' : entry.item.type === 'word' ? entry.item.title.slice(0, 1).toUpperCase() : entry.item.title}
                      </span>
                      <div>
                        <strong>{entry.item.title}</strong>
                        <small>{entry.forgotCount} 次忘记 · {entry.rate}% 易忘率</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="mini-empty">还没有易忘知识，继续保持</div>
                )}
              </div>
            </div>
          </article>

          {state.children.length > 1 ? (
            <article className="panel pk-panel">
              <SectionTitle eyebrow="双宝数据 PK" title="一起成长，也比一比进步" />
              <div className="child-tab-row">
                {state.children.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    className={child.id === activeChild.id ? 'is-active' : ''}
                    onClick={() => onSelectChild(child.id)}
                  >
                    <ChildAvatar child={child} size="small" active={child.id === activeChild.id} />
                    {child.name}
                  </button>
                ))}
              </div>
              <div className="pk-table">
                <div className="pk-table__head">
                  <span>数据</span>
                  {childStats.map((entry) => <strong key={entry.child.id}>{entry.child.name}</strong>)}
                </div>
                {[
                  { label: '当前星星', key: 'stars' as const, icon: <Star size={15} /> },
                  { label: '已掌握知识', key: 'mastered' as const, icon: <Medal size={15} /> },
                  { label: '今日复习', key: 'reviews' as const, icon: <BookOpen size={15} /> },
                  { label: '掌握率', key: 'masteryRate' as const, icon: <Trophy size={15} />, suffix: '%' }
                ].map((row) => {
                  const best = Math.max(...childStats.map((entry) => entry[row.key]));
                  return (
                    <div className="pk-table__row" key={row.key}>
                      <span>{row.icon}{row.label}</span>
                      {childStats.map((entry) => (
                        <strong key={entry.child.id} className={entry[row.key] === best ? 'is-winner' : ''}>
                          {entry[row.key]}{row.suffix || ''}
                          {entry[row.key] === best ? <Medal size={14} /> : null}
                        </strong>
                      ))}
                    </div>
                  );
                })}
              </div>
            </article>
          ) : null}
      </main>
    </div>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 11) return '早上好';
  if (hour < 14) return '中午好';
  if (hour < 18) return '下午好';
  return '晚上好';
}
